import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { Agency, ChainVerifyResult } from '../../lib/types';
import { ErrorNote, Field, PageHeader } from '../../ui/components';

const AGENCIES: Agency[] = ['SLTA', 'POLICE'];

const REASON_TEXT: Record<string, string> = {
  GAP: 'A sequence number is missing — the chain has a gap.',
  LINK_BROKEN: "A row's prevHash doesn't match the previous row's hash — the link between two notices is broken.",
  HASH_MISMATCH: "A row's own hash doesn't match its recomputed contents — this notice's core facts have changed since issuance.",
};

export function ChainVerify() {
  const [agency, setAgency] = useState<Agency>('SLTA');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ChainVerifyResult | null>(null);

  const verify = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const params = new URLSearchParams();
      if (from.trim()) params.set('from', from.trim());
      if (to.trim()) params.set('to', to.trim());
      const qs = params.toString();
      const res = await api.get<ChainVerifyResult>(`/api/v1/parking/contraventions/verify/${agency}${qs ? `?${qs}` : ''}`);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Chain Verify"
        subtitle="Recomputes each notice's hash and checks prev-hash linkage across the agency's sequence — proves the notice record hasn't been tampered with."
      />

      <div className="card max-w-xl p-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Agency">
            <select className="field-input" value={agency} onChange={(e) => setAgency(e.target.value as Agency)}>
              {AGENCIES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="From seq" hint="Optional — defaults to 1">
            <input className="field-input" inputMode="numeric" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To seq" hint="Optional — defaults to the latest">
            <input className="field-input" inputMode="numeric" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary mt-4 w-full" onClick={verify} disabled={busy}>
          {busy ? 'Verifying…' : 'Verify chain'}
        </button>

        {error ? (
          <div className="mt-4">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {result ? (
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
              result.valid ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'
            }`}
          >
            <div className="text-base font-bold">{result.valid ? 'PASS — chain intact' : 'FAIL — chain broken'}</div>
            <div className="mt-1 text-xs">
              {result.agency} · {result.checked} notice{result.checked === 1 ? '' : 's'} checked
            </div>
            {!result.valid ? (
              <div className="mt-2 text-xs">
                Broken at sequence #{result.brokenAtSeq} — {result.reason ? (REASON_TEXT[result.reason] ?? result.reason) : 'unknown reason'}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
